import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  MessageCircle,
  Mail,
  Activity,
  FileText,
  ChevronDown,
  RefreshCw,
  ExternalLink,
  Send,
  Loader2,
  CheckCircle2,
  AlertCircle,
  LifeBuoy,
} from 'lucide-react';
import { useSectionTranslation } from '@/hooks';
import { useDocumentTitle } from '@/hooks';
import { useLocale } from '@/lib/i18n';
import { api, isApiError, type ApiError } from '@/lib/api';
import { Header } from '@/features/home/Header';
import { Footer } from '@/features/home/Footer';
import { FormField } from '@/features/auth/components/FormField';
import { DiscordIcon } from '@/features/auth/components/BrandIcons';
import { m, AnimatePresence, LazyMotion, domAnimation } from 'framer-motion';
import { supportTranslations } from './translations';

type IconProps = { size?: number; className?: string };

type SupportTranslations = typeof supportTranslations['en-US'];

const DRAFT_KEY = 'supportDraft';
const STATUS_CACHE_TTL_MS = 60_000;

type ServiceStatus = 'ok' | 'degraded' | 'unavailable';

interface StatusCacheEntry {
  status: ServiceStatus;
  timestamp: string | null;
  fetchedAt: number;
}

let statusCache: StatusCacheEntry | null = null;

/* ── Validation ───────────────────────────────────────────────────── */
function buildContactSchema(errors: SupportTranslations['form']['errors']) {
  return z.object({
    name: z.string().trim().min(2, errors.nameTooShort).max(80, errors.nameTooLong),
    email: z
      .string()
      .trim()
      .min(1, errors.emailRequired)
      .email(errors.emailInvalid)
      .max(254, errors.emailTooLong),
    subject: z.string().trim().min(3, errors.subjectTooShort).max(120, errors.subjectTooLong),
    message: z.string().trim().min(10, errors.messageTooShort).max(2000, errors.messageTooLong),
    website: z.string().max(500).optional(),
  });
}

type ContactValues = z.infer<ReturnType<typeof buildContactSchema>>;

function isRateLimitError(res: ApiError): boolean {
  const code = (res.error?.code ?? '').toUpperCase();
  const message = (res.error?.message ?? '').toLowerCase();
  return (
    code === 'THROTTLER' ||
    code === 'TOO_MANY_REQUESTS' ||
    code === 'RATE_LIMITED' ||
    message.includes('too many')
  );
}

/* ── Service status hook ──────────────────────────────────────────── */
function useServiceStatus() {
  const [status, setStatus] = useState<ServiceStatus | null>(null);
  const [checkedAt, setCheckedAt] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchStatus = useCallback(async (force = false) => {
    if (!force && statusCache && Date.now() - statusCache.fetchedAt < STATUS_CACHE_TTL_MS) {
      setStatus(statusCache.status);
      setCheckedAt(statusCache.timestamp);
      setError(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(false);
    const res = await api.get<{ status: ServiceStatus; timestamp: string }>('/support/status');
    if (isApiError(res)) {
      setError(true);
      setLoading(false);
      return;
    }
    statusCache = {
      status: res.data.status,
      timestamp: res.data.timestamp ?? null,
      fetchedAt: Date.now(),
    };
    setStatus(res.data.status);
    setCheckedAt(res.data.timestamp ?? null);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const refresh = useCallback(() => fetchStatus(true), [fetchStatus]);

  return { status, checkedAt, error, loading, refresh };
}

/* ── Status widget ────────────────────────────────────────────────── */
function StatusWidget({
  status,
  checkedAtLabel,
  error,
  loading,
  onRefresh,
  t,
}: {
  status: ServiceStatus | null;
  checkedAtLabel: string | null;
  error: boolean;
  loading: boolean;
  onRefresh: () => void;
  t: SupportTranslations['status'];
}) {
  let dotClass = 'bg-brand-muted';
  let text = t.error;
  if (status === 'ok') {
    dotClass = 'bg-brand-success';
    text = t.ok;
  } else if (status === 'degraded') {
    dotClass = 'bg-amber-400';
    text = t.degraded;
  } else if (status === 'unavailable') {
    dotClass = 'bg-brand-danger';
    text = t.unavailable;
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {loading && !status ? (
            <Loader2 size={15} className="animate-spin text-brand-muted" aria-hidden />
          ) : (
            <span className={`h-2.5 w-2.5 rounded-full ${dotClass}`} aria-hidden />
          )}
          <span className={`text-sm font-medium ${error && !status ? 'text-brand-muted' : 'text-brand-white'}`}>
            {loading && !status ? t.checking : text}
          </span>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          aria-label={t.refreshLabel}
          disabled={loading}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-surface-border text-brand-muted hover:border-accent/30 hover:text-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>
      {checkedAtLabel && <p className="text-xs text-brand-muted">{checkedAtLabel}</p>}
    </div>
  );
}

/* ── Channel card ─────────────────────────────────────────────────── */
const channelVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0 },
};

function ChannelCard({
  icon: Icon,
  title,
  desc,
  cta,
  href,
  externalHref,
  children,
}: {
  icon: (props: IconProps) => ReactNode;
  title: string;
  desc: string;
  cta?: string;
  href?: string;
  externalHref?: string;
  children?: ReactNode;
}) {
  const isExternal = Boolean(externalHref);
  const isLink = isExternal || Boolean(href);

  const body = (
    <div className="relative z-10 flex h-full flex-col">
      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent/10 text-accent mb-5">
        <Icon size={24} />
      </div>
      <h3 className="text-base font-semibold text-brand-white mb-2">{title}</h3>
      <p className="text-sm text-brand-muted leading-relaxed">{desc}</p>
      {cta && (
        <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-accent">
          {cta}
          {isExternal && <ExternalLink size={14} />}
        </span>
      )}
      {children && <div className="mt-5">{children}</div>}
    </div>
  );

  return (
    <m.div
      variants={channelVariants}
      className="group relative rounded-2xl bg-surface-card border border-surface-border p-6 transition-colors overflow-hidden"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-accent/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
      {isExternal ? (
        <a href={externalHref} target="_blank" rel="noopener noreferrer" className="block h-full">
          {body}
        </a>
      ) : isLink ? (
        <Link to={href ?? ''} className="block h-full">
          {body}
        </Link>
      ) : (
        body
      )}
    </m.div>
  );
}

/* ── FAQ accordion ────────────────────────────────────────────────── */
function FaqItem({
  id,
  q,
  a,
  href,
  cta,
  open,
  onToggle,
}: {
  id: string;
  q: string;
  a: string;
  href: string;
  cta: string;
  open: boolean;
  onToggle: () => void;
}) {
  const buttonId = `faq-button-${id}`;
  const panelId = `faq-panel-${id}`;

  return (
    <div className="rounded-xl bg-surface-card border border-surface-border overflow-hidden">
      <button
        type="button"
        id={buttonId}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-3 px-5 py-4 text-start text-sm font-medium text-brand-white hover:bg-surface-hover/50 transition-colors"
      >
        <span>{q}</span>
        <m.span
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.25 }}
          className="shrink-0"
        >
          <ChevronDown size={16} className="text-brand-muted" />
        </m.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <m.div
            id={panelId}
            role="region"
            aria-labelledby={buttonId}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-4 space-y-3">
              <p className="text-sm text-brand-muted leading-relaxed">{a}</p>
              {href && (
                <Link
                  to={href}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-accent hover:underline"
                >
                  {cta}
                </Link>
              )}
            </div>
          </m.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ── Page ─────────────────────────────────────────────────────────── */
export function SupportPage() {
  const t = useSectionTranslation(supportTranslations);
  const { locale } = useLocale();
  useDocumentTitle(t.page);

  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [ticketId, setTicketId] = useState<string | null>(null);
  const [settings, setSettings] = useState<{ discordUrl: string; supportEmail: string }>({
    discordUrl: '',
    supportEmail: '',
  });

  const contactSchema = useMemo(() => buildContactSchema(t.form.errors), [t]);

  const { register, handleSubmit, reset, watch, formState: { errors, isSubmitting } } =
    useForm<ContactValues>({
      resolver: zodResolver(contactSchema),
      defaultValues: { name: '', email: '', subject: '', message: '', website: '' },
    });

  useEffect(() => {
    api
      .get<{ support?: { discordUrl?: string; supportEmail?: string } }>('/marketplace/settings')
      .then((res) => {
        if (!isApiError(res)) {
          setSettings({
            discordUrl: res.data?.support?.discordUrl ?? '',
            supportEmail: res.data?.support?.supportEmail ?? '',
          });
        }
      })
      .catch(() => {});
  }, []);

  // Footer links like /support#faq and /support#contact deep-link to sections.
  const location = useLocation();
  useEffect(() => {
    if (!location.hash) return;
    const target = document.getElementById(location.hash.slice(1));
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [location.hash]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<ContactValues>;
        reset({
          name: typeof parsed.name === 'string' ? parsed.name : '',
          email: typeof parsed.email === 'string' ? parsed.email : '',
          subject: typeof parsed.subject === 'string' ? parsed.subject : '',
          message: typeof parsed.message === 'string' ? parsed.message : '',
          website: '',
        });
      }
    } catch {
      // malformed draft — ignore and start fresh
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reset]);

  const draft = watch(['name', 'email', 'subject', 'message']);

  useEffect(() => {
    const [name, email, subject, message] = draft;
    const id = window.setTimeout(() => {
      try {
        localStorage.setItem(DRAFT_KEY, JSON.stringify({ name, email, subject, message }));
      } catch {
        // storage full or unavailable — draft persistence is best-effort
      }
    }, 300);
    return () => window.clearTimeout(id);
  }, [draft]);

  const { status, checkedAt, error, loading, refresh } = useServiceStatus();

  const timeFormatter = useMemo(
    () => new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }),
    [locale],
  );

  const checkedAtLabel = useMemo(() => {
    if (!checkedAt) return null;
    const d = new Date(checkedAt);
    if (!Number.isFinite(d.getTime())) return null;
    return t.status.checkedAt.replace('{time}', timeFormatter.format(d));
  }, [checkedAt, timeFormatter, t.status.checkedAt]);

  const hasDiscord = Boolean(settings.discordUrl.trim());
  const hasEmail = Boolean(settings.supportEmail.trim());

  const onSubmit = handleSubmit(async (values) => {
    setSubmitError(null);
    const res = await api.post<{ ticketId: string }>('/support/contact', values);
    if (isApiError(res)) {
      if (isRateLimitError(res)) {
        setSubmitError(t.form.errors.rateLimited);
      } else if (res.error?.code === 'NETWORK_ERROR') {
        setSubmitError(t.form.errors.network);
      } else {
        setSubmitError(res.error?.message || t.form.errors.generic);
      }
      return;
    }
    try {
      localStorage.removeItem(DRAFT_KEY);
    } catch {
      // ignore
    }
    setTicketId(res.data?.ticketId ?? '');
    reset({ name: '', email: '', subject: '', message: '', website: '' });
  });

  return (
    <LazyMotion features={domAnimation}>
      <div className="min-h-screen bg-surface-base">
        <Header />
        {/* Hero */}
        <section className="relative pt-28 pb-16 md:pt-36 md:pb-20">
          <div className="container-main relative z-10 text-center">
            <m.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="font-display text-display-lg text-brand-white mb-4"
            >
              {t.hero.heading}
            </m.h1>
            <m.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-body-md text-brand-muted max-w-xl mx-auto"
            >
              {t.hero.subtext}
            </m.p>
          </div>
        </section>

        {/* Channels */}
        <section className="pb-16 md:pb-20">
          <div className="container-main">
            <m.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8"
            >
              <h2 className="font-display text-display-md text-brand-white mb-2">{t.channels.heading}</h2>
              <p className="text-body-sm text-brand-muted">{t.channels.subtext}</p>
            </m.div>

            <m.div
              initial="hidden"
              animate="visible"
              transition={{ staggerChildren: 0.12 }}
              className={`grid grid-cols-1 gap-5 ${hasDiscord || hasEmail ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}
            >
              {hasDiscord && (
                <ChannelCard
                  icon={DiscordIcon}
                  title={t.channels.discord.title}
                  desc={t.channels.discord.desc}
                  cta={t.channels.discord.cta}
                  externalHref={settings.discordUrl}
                />
              )}
              {hasEmail && (
                <ChannelCard
                  icon={Mail}
                  title={t.channels.email.title}
                  desc={t.channels.email.desc}
                  cta={t.channels.email.cta}
                  externalHref={`mailto:${settings.supportEmail}`}
                />
              )}
              {!hasDiscord && !hasEmail && (
                <ChannelCard
                  icon={Send}
                  title={t.channels.fallback.title}
                  desc={t.channels.fallback.desc}
                />
              )}
              <ChannelCard
                icon={Activity}
                title={t.channels.status.title}
                desc={t.channels.status.desc}
              >
                <StatusWidget
                  status={status}
                  checkedAtLabel={checkedAtLabel}
                  error={error}
                  loading={loading}
                  onRefresh={refresh}
                  t={t.status}
                />
              </ChannelCard>
            </m.div>
          </div>
        </section>

        {/* Contact form */}
        <section id="contact" className="pb-16 md:pb-20 scroll-mt-24">
          <div className="container-main max-w-2xl mx-auto">
            <m.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              className="card-base rounded-2xl p-6 md:p-8"
            >
              <div className="flex items-center gap-2 mb-2">
                <LifeBuoy size={20} className="text-accent" />
                <h2 className="font-display text-display-md text-brand-white">{t.form.heading}</h2>
              </div>
              <p className="text-body-sm text-brand-muted mb-6">{t.form.subtext}</p>

              {ticketId !== null ? (
                <m.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="py-8 text-center"
                >
                  <CheckCircle2 size={44} className="mx-auto text-brand-success mb-4" aria-hidden />
                  <h3 className="font-display text-display-md text-brand-white mb-2">
                    {t.form.success.heading}
                  </h3>
                  <p className="text-body-sm text-brand-muted mb-4">{t.form.success.text}</p>
                  <p className="text-sm font-medium text-accent mb-1">
                    {t.form.success.ticketId.replace('{id}', ticketId)}
                  </p>
                  <p className="text-xs text-brand-muted">{t.form.success.reference}</p>
                </m.div>
              ) : (
                <form onSubmit={onSubmit} noValidate className="space-y-4">
                  <FormField
                    id="contact-name"
                    label={t.form.name}
                    placeholder={t.form.namePlaceholder}
                    autoComplete="name"
                    required
                    icon={<MessageCircle size={16} />}
                    error={errors.name?.message}
                    registration={register('name')}
                  />

                  <FormField
                    id="contact-email"
                    label={t.form.email}
                    type="email"
                    placeholder={t.form.emailPlaceholder}
                    autoComplete="email"
                    required
                    icon={<Mail size={16} />}
                    error={errors.email?.message}
                    registration={register('email')}
                  />

                  <FormField
                    id="contact-subject"
                    label={t.form.subject}
                    placeholder={t.form.subjectPlaceholder}
                    autoComplete="off"
                    required
                    icon={<FileText size={16} />}
                    error={errors.subject?.message}
                    registration={register('subject')}
                  />

                  <div className="space-y-1.5">
                    <label htmlFor="contact-message" className="text-xs font-medium text-brand-muted">
                      {t.form.message}
                      <span className="text-accent ms-1">*</span>
                    </label>
                    <textarea
                      id="contact-message"
                      rows={5}
                      placeholder={t.form.messagePlaceholder}
                      aria-invalid={Boolean(errors.message)}
                      aria-describedby={errors.message ? 'contact-message-error' : undefined}
                      {...register('message')}
                      className={`w-full rounded-lg bg-surface-card border px-3 py-2.5 text-sm text-brand-white placeholder:text-brand-muted/60 focus:outline-none transition-colors resize-none ${
                        errors.message
                          ? 'border-brand-danger/40'
                          : 'border-surface-border focus:border-accent/40'
                      }`}
                    />
                    {errors.message && (
                      <p
                        id="contact-message-error"
                        className="flex items-center gap-1 text-xs text-brand-danger"
                        role="alert"
                      >
                        <AlertCircle size={12} />
                        {errors.message.message}
                      </p>
                    )}
                  </div>

                  <div aria-hidden="true" className="absolute start-[-9999px] top-auto h-px w-px overflow-hidden">
                    <label htmlFor="contact-website">{t.form.website}</label>
                    <input
                      id="contact-website"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      {...register('website')}
                    />
                  </div>

                  {submitError && (
                    <div
                      className="flex items-center gap-2 rounded-lg border border-brand-danger/30 bg-brand-danger/10 px-3 py-2.5 text-sm text-brand-danger"
                      role="alert"
                    >
                      <AlertCircle size={16} />
                      {submitError}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary h-11 w-full disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        {t.form.submitting}
                      </>
                    ) : (
                      <>
                        <Send size={16} />
                        {t.form.submit}
                      </>
                    )}
                  </button>
                </form>
              )}
            </m.div>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="pb-20 md:pb-28 scroll-mt-24">
          <div className="container-main max-w-2xl mx-auto">
            <m.h2
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              className="font-display text-display-md text-brand-white mb-10 text-center"
            >
              {t.faq.heading}
            </m.h2>
            <m.div
              initial="hidden"
              animate="visible"
              transition={{ staggerChildren: 0.08 }}
              className="space-y-3"
            >
              {t.faq.items.map((item, i) => (
                <FaqItem
                  key={i}
                  id={String(i)}
                  q={item.q}
                  a={item.a}
                  href={item.href}
                  cta={item.cta}
                  open={openIndex === i}
                  onToggle={() => setOpenIndex(openIndex === i ? null : i)}
                />
              ))}
            </m.div>
          </div>
        </section>

        <Footer />
      </div>
    </LazyMotion>
  );
}
